import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { Link } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import {
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialog,
} from './alert-dialog'
import { Icon, type IconProps } from './icon'
import {
	TooltipTrigger,
	Tooltip,
	TooltipContent,
	TooltipProvider,
} from './tooltip'

export const buttonVariants = cva(
	cn(
		'inline-flex items-center justify-center rounded-md text-sm font-medium outline-none transition-colors',
		'focus-within:ring-2 focus-visible:ring-2',
		'ring-ring ring-offset-2 ring-offset-background',
		'disabled:pointer-events-none disabled:opacity-50',
	),
	{
		variants: {
			variant: {
				default: cn(
					'bg-primary text-primary-foreground',
					'hover:bg-primary/80',
				),
				destructive: cn(
					'bg-destructive/70',
					'hover:bg-destructive/90 hover:text-white',
				),
				outline: cn(
					'border border-input bg-background',
					'hover:bg-accent-foreground hover:text-white',
				),
				secondary: cn(
					'bg-secondary text-secondary-foreground',
					'hover:bg-secondary/80',
				),
				ghost: cn('hover:bg-accent/80 hover:text-accent-foreground'),
				link: cn(
					'text-primary underline-offset-4',
					'hover:underline',
					'disabled:text-muted-foreground',
				),
			},
			size: {
				default: 'h-10 px-4 py-2',
				wide: 'px-24 py-5',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				pill: 'px-12 py-3 leading-3',
				icon: 'h-8 w-8',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

export function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: ButtonProps) {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export type IconButtonProps = ButtonProps & {
	iconProps?: IconProps
	tooltipLabel?: string
}
export function IconButton({
	className,
	variant,
	size,
	iconProps,
	tooltipLabel,
	children,
	...props
}: IconButtonProps) {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						variant={variant ?? 'outline'}
						size={size ?? 'icon'}
						className={cn('rounded-full', className)}
						{...props}
					>
						{iconProps && <Icon {...iconProps} />}
						{children}
						{tooltipLabel && <span className="sr-only">{tooltipLabel}</span>}
					</Button>
				</TooltipTrigger>
				{tooltipLabel && <TooltipContent>{tooltipLabel}</TooltipContent>}
			</Tooltip>
		</TooltipProvider>
	)
}

export type IconButtonWithAlertDialogProps = IconButtonProps & {
	alertDialogTitle: React.ReactNode
	alertDialogDescription: React.ReactNode
	alertDialogTrigger?: React.ReactNode
	alertDialogContent: React.ReactNode
	alertDialogCancel?: React.ReactNode
	alertDialogAction?: React.ReactNode
}

export function IconButtonWithAlertDialog({
	alertDialogTitle,
	alertDialogDescription,
	alertDialogContent,
	alertDialogCancel,
	alertDialogAction,
	...iconButtonProps
}: IconButtonWithAlertDialogProps) {
	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<IconButton {...iconButtonProps} />
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{alertDialogTitle}</AlertDialogTitle>
					<AlertDialogDescription>
						{alertDialogDescription}
					</AlertDialogDescription>
				</AlertDialogHeader>
				{alertDialogContent}
				<AlertDialogFooter>
					{alertDialogCancel}
					{alertDialogAction}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

type ButtonLinkProps = ButtonProps & { to: string; tooltipLabel?: string }
export function ButtonLink({
	children,
	to,
	tooltipLabel,
	...props
}: ButtonLinkProps) {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button asChild {...props}>
						<Link to={to} className="flex items-center gap-2">
							{children}
						</Link>
					</Button>
				</TooltipTrigger>
				{tooltipLabel ? <TooltipContent>{tooltipLabel}</TooltipContent> : null}
			</Tooltip>
		</TooltipProvider>
	)
}

type IconButtonLinkProps = IconButtonProps & { to: string }
export function IconButtonLink({
	children,
	to,
	iconProps,
	...props
}: IconButtonLinkProps) {
	return (
		<ButtonLink
			to={to}
			variant="outline"
			size="icon"
			className="rounded-full"
			{...props}
		>
			{iconProps && <Icon {...iconProps} />}
			{children}
		</ButtonLink>
	)
}
